import { useEffect, useState } from 'react'
import { PageHead } from '@components/PageHead'
import { useTwoFactor } from '@features/auth/hooks/useTwoFactor'
import { useUserPreferences } from '@features/preferences/hooks/useUserPreferences'
import { THEME_VALUES, type ThemePreference } from '@features/preferences/types'
import { Notice } from '@shared/ui/Notice'
import { Toggle } from '@shared/ui/Toggle'
import { useToast } from '@shared/ui/useToast'
import { Icon, type IconName } from '@/icons/Icon'
import { useTranslation } from '@/i18n'
import { AccountSection } from './settings/AccountSection'
import { ChangeMasterPasswordSection } from './settings/ChangeMasterPasswordSection'
import { DeleteAccountPageSection } from './settings/DeleteAccountPageSection'
import { PinUnlockSection } from './settings/PinUnlockSection'
import { DomainSection } from './settings/DomainSection'
import { EmergencySection } from './settings/EmergencySection'
import { RotateRecoveryKeySection } from './settings/RotateRecoveryKeySection'
import { TrustedDevicesSection } from './settings/TrustedDevicesSection'
import { TwoFactorSection } from './settings/TwoFactorSection'

/**
 * 設定ページ (T-D-Settings)。design/js/screens-settings.jsx を移植。
 *
 * - サイドバーナビ (settings-nav) でセクションを切り替える
 * - window.location.hash で settings/:sub に遷移してブックマーク可にする
 * - Preferences → 言語ピッカーのみ実際に動作する (setLanguage 経由)
 * - その他セクションは Phase 4 実装予定のスタブ
 */

type SectionKey =
  | 'account'
  | 'security'
  | '2fa'
  | 'emergency'
  | 'preferences'
  | 'domain'
  | 'delete-account'

const SECTIONS: readonly { key: SectionKey; icon: IconName; labelKey: string }[] = [
  { key: 'account', icon: 'user', labelKey: 'common.settings_sub.account' },
  { key: 'security', icon: 'key', labelKey: 'common.settings_sub.security' },
  { key: '2fa', icon: 'shield', labelKey: 'common.settings_sub.2fa' },
  { key: 'emergency', icon: 'shield-alert', labelKey: 'common.settings_sub.emergency' },
  { key: 'preferences', icon: 'settings', labelKey: 'common.settings_sub.preferences' },
  { key: 'domain', icon: 'lock', labelKey: 'common.settings_sub.domain' },
  { key: 'delete-account', icon: 'trash', labelKey: 'common.settings_sub.delete_account' },
]

interface SettingRowProps {
  label: string
  sub?: string
  ctrl: JSX.Element
}

const SettingRow = ({ label, sub, ctrl }: SettingRowProps): JSX.Element => (
  <div className="setting-row">
    <div>
      <div className="setting-row-label">{label}</div>
      {sub != null && <div className="setting-row-sub">{sub}</div>}
    </div>
    {ctrl}
  </div>
)

interface SectionHeadProps {
  title: string
  sub: string
}

const SectionHead = ({ title, sub }: SectionHeadProps): JSX.Element => (
  <div className="settings-section-head">
    <h2 className="settings-section-title">{title}</h2>
    <div className="settings-section-sub">{sub}</div>
  </div>
)

export interface SettingsPageProps {
  route: string
}

export const SettingsPage = ({ route }: SettingsPageProps): JSX.Element => {
  const { t, language, setLanguage } = useTranslation()

  const routeSub = route.startsWith('settings/') ? route.split('/')[1] : undefined
  const validSub = SECTIONS.some(s => s.key === routeSub) ? (routeSub as SectionKey) : undefined
  const [activeSection, setActiveSection] = useState<SectionKey>(validSub ?? 'account')

  // #6 fix: route prop が外部から変わったら (例: 緊急連絡先「管理」button click で
  // window.location.hash 経由) activeSection を追従させる。
  useEffect(() => {
    if (validSub && validSub !== activeSection) {
      setActiveSection(validSub)
    }
  }, [validSub, activeSection])

  const [changePwOpen, setChangePwOpen] = useState(false)
  const [rotateRecoveryOpen, setRotateRecoveryOpen] = useState(false)
  const [trustedDevicesOpen, setTrustedDevicesOpen] = useState(false)
  const [pinUnlockOpen, setPinUnlockOpen] = useState(false)

  // 2FA 状態は #settings/security の TOTP 行に live 表示するため、設定画面 mount 時に
  // 1 回だけ status を取得する (TwoFactorSection が #settings/2fa で別途使う useTwoFactor
  // とは独立した instance だが、`/auth/2fa/status` の取得 cost は微小なので許容)。
  // hook が未認証で 401 を返した場合は `enabled=false` 扱いで描画する (誤って「有効」
  // 表示しないことが重要)。
  const twoFactor = useTwoFactor()
  const twoFactorEnabled = twoFactor.status?.enabled ?? false

  // Track D: User preferences (theme / idle / clipboard / favorites / merge)
  const { preferences, setPreference } = useUserPreferences()
  const { show: showToast } = useToast()

  // 各 setPreference 呼出を Toast 連動でラップする (失敗時に danger Toast)
  const updatePreference = async <K extends keyof typeof preferences>(
    key: K,
    value: (typeof preferences)[K],
  ): Promise<void> => {
    try {
      await setPreference(key, value)
    } catch {
      showToast({ titleKey: 'send.error.network', tone: 'danger', dedupe: true })
    }
  }

  // idle / clipboard timeout の選択肢 — ms / sec の対応表で管理
  // idleLockMs === 0 は「ブラウザを閉じたときのみロック」(timer 無効)
  // 値は ookey-ios (1/5/15/30/60 分) と同期する — sync しないと両端末で
  // option が食い違って混乱するため。
  const idleOptions: readonly { value: number; labelKey: string }[] = [
    { value: 1 * 60 * 1000, labelKey: 'settings.preferences.idle_1min' },
    { value: 5 * 60 * 1000, labelKey: 'settings.preferences.idle_5min' },
    { value: 15 * 60 * 1000, labelKey: 'settings.preferences.idle_15min' },
    { value: 30 * 60 * 1000, labelKey: 'settings.preferences.idle_30min' },
    { value: 60 * 60 * 1000, labelKey: 'settings.preferences.idle_1h' },
    { value: 0, labelKey: 'settings.preferences.idle_browser_close' },
  ]

  const clipboardOptions: readonly { value: number; labelKey: string }[] = [
    { value: 30, labelKey: 'settings.preferences.clip_30sec' },
    { value: 60, labelKey: 'settings.preferences.clip_60sec' },
    { value: 90, labelKey: 'settings.preferences.clip_90sec' },
    { value: 180, labelKey: 'settings.preferences.clip_180sec' },
  ]

  const navigate = (key: SectionKey): void => {
    setActiveSection(key)
    window.location.hash = `settings/${key}`
  }

  return (
    <div className="settings-page">
      <PageHead title={t('settings.title')} sub={t('settings.sub')} />

      <div className="settings-layout page-body flush">
        <div className="settings-nav">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              type="button"
              className={`nav-item${activeSection === s.key ? ' active' : ''}`}
              onClick={() => {
                navigate(s.key)
              }}
            >
              <span className="ico">
                <Icon name={s.icon} aria-hidden="true" />
              </span>
              <span className="lbl">{t(s.labelKey)}</span>
            </button>
          ))}
        </div>

        <div className="settings-content">
          {activeSection === 'account' && (
            <div className="settings-section-body">
              <SectionHead title={t('settings.account.title')} sub={t('settings.account.sub')} />
              <AccountSection />
            </div>
          )}

          {activeSection === 'security' && (
            <div className="settings-section-body">
              <SectionHead title={t('settings.security.title')} sub={t('settings.security.sub')} />
              <Notice tone="info">{t('settings.security.stub_notice')}</Notice>

              <div className="card settings-security-item">
                <div className="card-row-flex">
                  <div>
                    <div className="settings-item-label">
                      {t('settings.security.change_master_pw')}
                    </div>
                    <div className="settings-item-sub">
                      {t('settings.security.change_master_pw_sub')}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setChangePwOpen(v => !v)
                    }}
                    aria-expanded={changePwOpen}
                    data-testid="change-master-pw-toggle"
                  >
                    {changePwOpen
                      ? t('settings.security.change_master_pw_cancel')
                      : t('settings.security.manage')}
                  </button>
                </div>
                {changePwOpen && (
                  <div className="settings-security-item-body">
                    <ChangeMasterPasswordSection />
                  </div>
                )}
              </div>

              <div className="card settings-security-item">
                <div className="card-row-flex">
                  <div>
                    <div className="settings-item-label">{t('settings.security.totp_item')}</div>
                    <div className="settings-item-sub">{t('settings.security.totp_item_sub')}</div>
                  </div>
                  {twoFactor.loading ? (
                    <span className="tag">…</span>
                  ) : twoFactorEnabled ? (
                    <span className="tag good">
                      <Icon name="check" aria-hidden="true" width={12} height={12} />
                      {t('settings.security.enabled')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        navigate('2fa')
                      }}
                    >
                      {t('settings.security.manage')}
                    </button>
                  )}
                </div>
              </div>

              <div className="card settings-security-item">
                <div className="card-row-flex">
                  <div>
                    <div className="settings-item-label">
                      {t('settings.security.emergency_item')}
                    </div>
                    <div className="settings-item-sub">
                      {t('settings.security.emergency_item_sub')}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      window.location.hash = 'settings/emergency'
                    }}
                    data-testid="emergency-manage"
                  >
                    {t('settings.security.manage')}
                  </button>
                </div>
              </div>

              <div className="card settings-security-item">
                <div className="card-row-flex">
                  <div>
                    <div className="settings-item-label">
                      {t('settings.security.recovery_key_item')}
                    </div>
                    <div className="settings-item-sub">
                      {t('settings.security.recovery_key_item_sub')}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setRotateRecoveryOpen(v => !v)
                    }}
                    aria-expanded={rotateRecoveryOpen}
                    data-testid="rotate-recovery-toggle"
                  >
                    {rotateRecoveryOpen
                      ? t('settings.security.rotate_recovery_key_cancel')
                      : t('settings.security.manage')}
                  </button>
                </div>
                {rotateRecoveryOpen && (
                  <div className="settings-security-item-body">
                    <RotateRecoveryKeySection
                      onClose={() => {
                        setRotateRecoveryOpen(false)
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="card settings-security-item">
                <div className="card-row-flex">
                  <div>
                    <div className="settings-item-label">
                      {t('settings.security.pin_unlock_title')}
                    </div>
                    <div className="settings-item-sub">{t('settings.security.pin_unlock_sub')}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setPinUnlockOpen(v => !v)
                    }}
                    aria-expanded={pinUnlockOpen}
                    data-testid="pin-unlock-toggle"
                  >
                    {pinUnlockOpen
                      ? t('settings.security.change_master_pw_cancel')
                      : t('settings.security.manage')}
                  </button>
                </div>
                {pinUnlockOpen && (
                  <div className="settings-security-item-body">
                    <PinUnlockSection />
                  </div>
                )}
              </div>

              <div className="card settings-security-item">
                <div className="card-row-flex">
                  <div>
                    <div className="settings-item-label">
                      {t('settings.security.trusted_devices')}
                    </div>
                    <div className="settings-item-sub">
                      {t('settings.security.trusted_devices_sub')}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setTrustedDevicesOpen(v => !v)
                    }}
                    aria-expanded={trustedDevicesOpen}
                    data-testid="trusted-devices-toggle"
                  >
                    {trustedDevicesOpen
                      ? t('settings.security.trusted_devices_close')
                      : t('settings.security.manage')}
                  </button>
                </div>
                {trustedDevicesOpen && (
                  <div className="settings-security-item-body">
                    <TrustedDevicesSection />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === '2fa' && (
            <div className="settings-section-body">
              <SectionHead title={t('settings.2fa.title')} sub={t('settings.2fa.sub')} />
              <TwoFactorSection />
            </div>
          )}

          {activeSection === 'emergency' && (
            <div className="settings-section-body">
              <SectionHead
                title={t('settings.emergency.title')}
                sub={t('settings.emergency.sub')}
              />
              <EmergencySection />
            </div>
          )}

          {activeSection === 'preferences' && (
            <div className="settings-section-body">
              <SectionHead
                title={t('settings.preferences.title')}
                sub={t('settings.preferences.sub')}
              />

              <div className="card settings-pref-card">
                <div className="settings-card-title">{t('settings.preferences.display_title')}</div>
                <SettingRow
                  label={t('settings.preferences.theme')}
                  ctrl={
                    <select
                      className="select settings-select"
                      value={preferences.theme}
                      onChange={ev => {
                        void updatePreference('theme', ev.target.value as ThemePreference)
                      }}
                    >
                      {THEME_VALUES.map(v => (
                        <option key={v} value={v}>
                          {t(`settings.preferences.theme_${v}`)}
                        </option>
                      ))}
                    </select>
                  }
                />
                <SettingRow
                  label={t('settings.preferences.language')}
                  ctrl={
                    <select
                      className="select settings-select"
                      value={language}
                      onChange={ev => {
                        const next = ev.target.value as 'ja' | 'en'
                        setLanguage(next)
                        void updatePreference('language', next)
                      }}
                    >
                      <option value="ja">{t('settings.preferences.language_ja')}</option>
                      <option value="en">{t('settings.preferences.language_en')}</option>
                    </select>
                  }
                />
                <SettingRow
                  label={t('settings.preferences.favorites_top')}
                  ctrl={
                    <Toggle
                      on={preferences.favoritesOnTop}
                      onChange={next => void updatePreference('favoritesOnTop', next)}
                      ariaLabel={t('settings.preferences.favorites_top')}
                    />
                  }
                />
                <SettingRow
                  label={t('settings.preferences.merge_domains')}
                  ctrl={
                    <Toggle
                      on={preferences.mergeDomains}
                      onChange={next => void updatePreference('mergeDomains', next)}
                      ariaLabel={t('settings.preferences.merge_domains')}
                    />
                  }
                />
              </div>

              <div className="card settings-pref-card">
                <div className="settings-card-title">{t('settings.preferences.session_title')}</div>
                <SettingRow
                  label={t('settings.preferences.session_timeout')}
                  sub={t('settings.preferences.session_timeout_sub')}
                  ctrl={
                    <select
                      className="select settings-select"
                      value={preferences.idleLockMs}
                      onChange={ev => {
                        void updatePreference('idleLockMs', Number(ev.target.value))
                      }}
                    >
                      {idleOptions.map(o => (
                        <option key={o.value} value={o.value}>
                          {t(o.labelKey)}
                        </option>
                      ))}
                    </select>
                  }
                />
                <SettingRow
                  label={t('settings.preferences.clipboard_clear')}
                  sub={t('settings.preferences.clipboard_clear_sub')}
                  ctrl={
                    <select
                      className="select settings-select"
                      value={preferences.clipboardTtlSec}
                      onChange={ev => {
                        void updatePreference('clipboardTtlSec', Number(ev.target.value))
                      }}
                    >
                      {clipboardOptions.map(o => (
                        <option key={o.value} value={o.value}>
                          {t(o.labelKey)}
                        </option>
                      ))}
                    </select>
                  }
                />
              </div>

              <div className="card settings-pref-card">
                <div className="settings-card-title">{t('settings.preferences.app_title')}</div>
                <SettingRow
                  label={t('settings.preferences.autofill')}
                  sub={t('settings.preferences.autofill_sub')}
                  ctrl={
                    <Toggle
                      on={preferences.autofill}
                      onChange={next => void updatePreference('autofill', next)}
                      ariaLabel={t('settings.preferences.autofill')}
                    />
                  }
                />
                <SettingRow
                  label={t('settings.preferences.notifications')}
                  sub={t('settings.preferences.notifications_sub')}
                  ctrl={
                    <Toggle
                      on={preferences.notifications}
                      onChange={next => void updatePreference('notifications', next)}
                      ariaLabel={t('settings.preferences.notifications')}
                    />
                  }
                />
              </div>
            </div>
          )}

          {activeSection === 'domain' && (
            <div className="settings-section-body">
              <SectionHead title={t('settings.domain.title')} sub={t('settings.domain.sub')} />
              <DomainSection />
            </div>
          )}

          {activeSection === 'delete-account' && (
            <div className="settings-section-body">
              <SectionHead
                title={t('settings.account.delete.section_title')}
                sub={t('settings.account.delete.section_desc')}
              />
              <DeleteAccountPageSection />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
